import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { S, StatusBadge, DataCard, formatCurrency } from '../../regureStyles';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

const groupByPeriod = (records) => {
  const map = new Map();
  for (const r of records) {
    if (!map.has(r.period)) {
      map.set(r.period, { period: r.period, recurring: 0, acquisition: 0, total: 0, paid: 0, statuses: new Set() });
    }
    const row = map.get(r.period);
    row.total += r.amount || 0;
    if (r.type === 'recurring') row.recurring += r.amount || 0;
    if (r.type === 'acquisition') row.acquisition += r.amount || 0;
    row.paid += r.paidAmount || 0;
    row.statuses.add(r.status);
  }
  return Array.from(map.values())
    .map(r => ({
      ...r,
      outstanding: Math.max(0, r.total - r.paid),
      status: r.statuses.has('paid') && r.statuses.size === 1
        ? 'paid'
        : r.statuses.has('approved') ? 'approved' : 'earned',
    }))
    .sort((a, b) => (a.period < b.period ? 1 : -1));
};

const CommissionHistory = ({ b }) => {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const brokerId = b?._id || b?.id;

  useEffect(() => {
    if (!brokerId) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await axios.get(
          `${BASE_URL}/commissions/records?broker=${brokerId}&limit=200`,
          { withCredentials: true }
        );
        const records = Array.isArray(resp.data?.data) ? resp.data.data : [];
        if (!cancelled) setRows(groupByPeriod(records));
      } catch {
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [brokerId]);

  const openStatement = (period) => {
    window.location.assign(`/commissions?broker=${brokerId}&period=${encodeURIComponent(period)}`);
  };

  return (
    <DataCard style={{ marginBottom: 20 }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: S.textPrimary, marginBottom: 14 }}>
        {t('screens.brokers.commissionHistoryTitle', { defaultValue: 'Commission History' })}
      </div>
      {loading ? (
        <div style={{ padding: 20, color: S.textMuted, fontSize: 13 }}>{t('screens.brokers.commissionLoading', { defaultValue: 'Loading…' })}</div>
      ) : rows.length === 0 ? (
        <div style={{ padding: 20, color: S.textMuted, fontSize: 13 }}>
          {t('screens.brokers.commissionEmpty', { defaultValue: 'No commission records yet.' })}
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {[
                t('screens.brokers.commissionColPeriod',       { defaultValue: 'Period' }),
                t('screens.brokers.commissionColRecurring',    { defaultValue: 'Recurring' }),
                t('screens.brokers.commissionColAcquisition',  { defaultValue: 'Acquisition' }),
                t('screens.brokers.commissionColTotalEarned',  { defaultValue: 'Total Earned' }),
                t('screens.brokers.commissionColPaid',         { defaultValue: 'Paid' }),
                t('screens.brokers.commissionColOutstanding',  { defaultValue: 'Outstanding' }),
                t('screens.brokers.commissionColStatus',       { defaultValue: 'Status' }),
              ].map(h => (
                <th
                  key={h}
                  style={{
                    padding: '10px 12px',
                    textAlign: 'left',
                    fontSize: 11,
                    fontWeight: 700,
                    color: S.textMuted,
                    textTransform: 'uppercase',
                    letterSpacing: 0.8,
                    borderBottom: `2px solid ${S.borderLight}`,
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr
                key={r.period}
                style={{
                  borderBottom: `1px solid ${S.borderLight}`,
                  background: i % 2 === 0 ? '#fff' : S.bgCard,
                  cursor: 'pointer',
                }}
                onClick={() => openStatement(r.period)}
                onMouseEnter={e => (e.currentTarget.style.background = S.bgHover)}
                onMouseLeave={e => (e.currentTarget.style.background = i % 2 === 0 ? '#fff' : S.bgCard)}
              >
                <td style={{ padding: '11px 12px', fontSize: 13, fontWeight: 600, color: S.textPrimary }}>{r.period}</td>
                <td style={{ padding: '11px 12px', fontSize: 13, color: S.textPrimary }}>{formatCurrency(r.recurring)}</td>
                <td style={{ padding: '11px 12px', fontSize: 13, color: S.burgundy, fontWeight: 600 }}>{formatCurrency(r.acquisition)}</td>
                <td style={{ padding: '11px 12px', fontSize: 13, fontWeight: 700, color: S.textPrimary }}>{formatCurrency(r.total)}</td>
                <td style={{ padding: '11px 12px', fontSize: 13, color: '#10b981' }}>{formatCurrency(r.paid)}</td>
                <td style={{ padding: '11px 12px', fontSize: 13, fontWeight: 600, color: r.outstanding > 0 ? '#f59e0b' : S.textMuted }}>
                  {r.outstanding > 0 ? formatCurrency(r.outstanding) : '—'}
                </td>
                <td style={{ padding: '11px 12px' }}>
                  <StatusBadge status={r.status} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </DataCard>
  );
};

export default CommissionHistory;
